import { createModel } from '@rematch/core';
import { formatProviderPlanConfiguration, ProviderPlanConfiguration } from '../../modules/provider/ProviderPlanConfiguration';
import * as appointmentVariation from '../../modules/provider/appointmentVariation';
import { ProviderPlan } from '../../types/provider';
import {
  billingHistoryHasPendingPayment,
  buildBillingHistoryItems,
  getDaysRemaining,
  getIntoLateTrial,
  getPrice,
  getStatusFromSubscriptionState,
  SubscriptionStatus,
  trackSubscriptionSuccess,
} from '../../modules/provider/Subscription';
import { IProviderSignupSettingsData } from '../../modules/Api.types';
import {
  loadSignupSettings,
  reenrollProSubscription,
  startProSubscription,
  updateStripeInstrument,
} from '../../api/Providers/SignupSettings';
import type { RootModel } from '../models';
import {
  APISubscriptionCharge,
  getSubscriptionCharges,
  SubscriptionChargeStatus,
} from '../../api/Providers/SubscriptionCharges';
import analytics from '../../modules/analytics';
import { IProMembershipProgram, updateProviderSelectedPlan } from '../../api/Providers/Plan';
import { InstrumentFilter } from '../../api/Users/Instruments';
import { ICard } from '../PaymentMethods';
import {
  APISubscriptionCoupon,
  applySubscriptionCoupon,
  fetchSubscriptionCoupon,
} from '../../api/Providers/SubscriptionCoupon';
import nonCriticalException from '../../modules/exceptionLogger';
import { ProviderRecord } from '../Providers.model';
import { PrivilegedProvider } from '../../api/Providers';
import { getProMembershipPrograms } from '../../api/ProMembershipPrograms';

const buildStoredPaymentMethod = (storedMethodId: number, methods: ICard[]): ICard | null => (
  ((methods || []).find(method => method.id === storedMethodId) || null)
);

interface DerivedProviderPlanState {
  status: SubscriptionStatus;
  data: IProviderSignupSettingsData;
  provider_id: number;
  price: number;
  subscription_state: string | null;
  daysRemaining: number;
  isIntoLateTrial: boolean;
  requiresPaymentUpdate: boolean;
  charges: APISubscriptionCharge[];
  coupon: APISubscriptionCoupon | undefined;
  planConfigurations: Array<IProMembershipProgram>;
}

interface LoadingProviderPlanState extends Partial<DerivedProviderPlanState> {
  loading: true;
}

interface LoadedProviderPlanState extends DerivedProviderPlanState {
  loading: false;
}

export type ProviderPlanState = LoadingProviderPlanState | LoadedProviderPlanState;

function getInitialState(): ProviderPlanState {
  return {
    loading: true, charges: [],
  };
}

type OnPlanLoadedPayload = {
  providerId: number;
  signupSettings: IProviderSignupSettingsData;
};

type LoadPlanPayload = {
  providerId: number;
};

type UpdatePlanPayload = {
  providerId: number;
  providerPlan: string;
  plan: ProviderPlanConfiguration;
  isRestoringSubscription?: boolean;
  paymentMethod?: ICard;
};

type ApplyCouponPayload = {
  providerId?: number;
  couponId?: string;
};

type LoadCouponPayload = {
  providerId: number;
  couponId: string;
};

export type SubscriptionCharge = Pick<APISubscriptionCharge,
'id' | 'amount_dollars' | 'external_created'> & {
  status: SubscriptionChargeStatus | 'upcoming';
  source?: string;
};

export const providerPlan = createModel<RootModel>()({
  name: 'providerPlan',
  state: getInitialState(),
  reducers: {
    onPlanLoading: (state: ProviderPlanState): ProviderPlanState => ({
      ...state,
      loading: true,
      charges: [],
    }),
    onPlanLoaded: (state: ProviderPlanState, payload: OnPlanLoadedPayload): ProviderPlanState => {
      const { providerId, signupSettings } = payload;
      return {
        ...state,
        loading: false,
        charges: state.charges || [],
        coupon: state.coupon || undefined,
        status: getStatusFromSubscriptionState(signupSettings.subscription_state),
        data: signupSettings,
        provider_id: providerId,
        price: getPrice(signupSettings),
        subscription_state: signupSettings.subscription_state,
        daysRemaining: getDaysRemaining(signupSettings),
        isIntoLateTrial: getIntoLateTrial(signupSettings),
        requiresPaymentUpdate: signupSettings.requires_payment_update,
        planConfigurations: state.planConfigurations,
      };
    },
    onChargesLoaded: (
      state: ProviderPlanState,
      payload: APISubscriptionCharge[],
    ): ProviderPlanState => ({
      ...state,
      charges: payload,
    }),
    onCouponLoaded: (
      state: ProviderPlanState,
      payload: APISubscriptionCoupon,
    ): ProviderPlanState => ({
      ...state,
      coupon: payload,
    }),
    onPlanConfigurationsLoaded: (
      state: ProviderPlanState,
      payload: Array<IProMembershipProgram>,
    ): ProviderPlanState => ({
      ...state,
      planConfigurations: payload,
    }),
  },
  effects: dispatch => ({
    async loadPlan(
      { providerId }: LoadPlanPayload,
    ): Promise<void> {
      await dispatch.providerPlan.onPlanLoading();
      await dispatch.subsBlocker.update();
      const signupSettings = await loadSignupSettings(providerId);

      const charges = await getSubscriptionCharges(providerId);
      await dispatch.providerPlan.loadPlanConfigurationData();
      await dispatch.providerPlan.onChargesLoaded(charges);
      await dispatch.paymentMethods.loadFiltered(InstrumentFilter.ChargeFee);

      await dispatch.providerPlan.onPlanLoaded({ providerId, signupSettings });
    },

    async applyCoupon({ providerId, couponId }: ApplyCouponPayload): Promise<void> {
      if (!providerId || !couponId) {
        return;
      }

      try {
        const coupon = await applySubscriptionCoupon(providerId, couponId);
        dispatch.providerPlan.onCouponLoaded(coupon);
      } catch (err) {
        // NOOP: No matching coupon found
        if (err?.code === 404) return;

        nonCriticalException(err);
      }
    },

    async loadCoupon({
      providerId,
      couponId,
    }: LoadCouponPayload): Promise<void> {
      if (!providerId || !couponId) {
        return null;
      }

      try {
        const coupon = await fetchSubscriptionCoupon(providerId, couponId);
        dispatch.providerPlan.onCouponLoaded(coupon);
      } catch (err) {
        if (err?.code === 404) return null; // No matching coupon found

        nonCriticalException(err);
      }
    },

    async loadPlanConfigurationData(): Promise<void> {
      const canShowPremiumPlanUpsell = await appointmentVariation.allowed({ checkFlagOnly: true });
      let planConfigurationData = await getProMembershipPrograms();
      if (!canShowPremiumPlanUpsell) {
        // no premium when flag is on
        planConfigurationData = planConfigurationData.filter(
          p => p.plan_name !== ProviderPlan.Deluxe,
        );
      }
      dispatch.providerPlan.onPlanConfigurationsLoaded(planConfigurationData);
    },

    async updateProviderSelectedPlan(
      payload: { providerId: number; plan: string },
    ): Promise<Response> {
      const { providerId, plan } = payload;
      return updateProviderSelectedPlan(providerId, plan);
    },

    async updatePlan(payload: UpdatePlanPayload, rootState): Promise<void> {
      const {
        plan,
        providerPlan: currentPlan,
        providerId,
        isRestoringSubscription,
      } = payload;

      // We're restoring the pro
      if (isRestoringSubscription) {
        await dispatch.subsBlocker
          .unblock({
            plan,
            stripeInstrumentId: payload.paymentMethod?.id || null,
          });
        await dispatch.user.whoami({ force: true });

        analytics.track('pro_restoration_successful', {
          plan: plan.plan_name,
          eligibleForTrial: false,
        });

        return;
      }

      // Pro's plan is changing
      if (plan && plan.plan_name !== currentPlan) {
        // Update the plan, then the payment instrument if necessary
        await dispatch.providerPlan.updateProviderSelectedPlan({
          providerId,
          plan: plan.plan_name,
        });

        if (payload.paymentMethod && plan.numericAmount > 0) {
          await updateStripeInstrument(providerId, payload.paymentMethod.id);
        }

        await dispatch.user.whoami({ force: true });
        await dispatch.providerPlan.loadPlan({
          providerId,
        });

        return;
      }

      const subscriptionStatus = (
        rootState.providerPlan.loading
          ? null
          : (rootState.providerPlan as LoadedProviderPlanState).status
      );

      // if the subscription is "active"
      if (
        payload.paymentMethod && subscriptionStatus === SubscriptionStatus.Active) {
        // Update the payment instrument
        await updateStripeInstrument(providerId, payload.paymentMethod.id);

        await dispatch.providerPlan.loadPlan({
          providerId,
        });
        trackSubscriptionSuccess(rootState.providerPlan.data, payload.paymentMethod, false);
        return;
      }

      if (subscriptionStatus === SubscriptionStatus.Canceled) {
        // uncancel
        await reenrollProSubscription(providerId, payload.paymentMethod?.id);
        await dispatch.providerPlan.loadPlan({
          providerId,
        });
        trackSubscriptionSuccess(rootState.providerPlan.data, payload.paymentMethod, false);
        return;
      }
      // Status is in trial or expired

      // Subscribe pro and set payment instrument
      await startProSubscription(providerId, payload.paymentMethod?.id);
      await dispatch.providerPlan.loadPlan({
        providerId,
      });
      trackSubscriptionSuccess(rootState.providerPlan.data, payload.paymentMethod, true);
    },
  }),
  selectors: (slice, createSelector) => ({
    billingHistoryItems() {
      return createSelector(
        slice(s => s.status),
        slice(s => s.data),
        slice(s => s.loading),
        slice(s => s.charges),
        buildBillingHistoryItems,
      );
    },

    storedPaymentMethod() {
      return createSelector(
        slice(s => s?.data?.preview_subscription_stripe_instrument),
        state => state.paymentMethods.methods,
        buildStoredPaymentMethod,
      );
    },

    hasPendingPayment() {
      return createSelector(
        slice(s => s.status),
        slice(s => s.data),
        slice(s => s.loading),
        slice(s => s.charges),
        slice(s => s?.data?.preview_subscription_stripe_instrument),
        state => state.paymentMethods.methods,
        (status, data, loading, charges, storedMethodId, methods): boolean => {
          const billingHistoryItems = buildBillingHistoryItems(status, data, loading, charges);
          const storedPaymentMethod = buildStoredPaymentMethod(storedMethodId, methods);

          return !!storedPaymentMethod && billingHistoryHasPendingPayment(
            billingHistoryItems,
            storedPaymentMethod,
          );
        },
      );
    },

    currentPlanConfiguration() {
      return createSelector(
        rootState => (
          rootState.providers.providersById as Record<number, ProviderRecord>
        ),
        slice(s => s.provider_id),
        slice(s => s.planConfigurations),
        (
          providersById: Record<number, ProviderRecord>,
          providerId: number,
          configurations: IProMembershipProgram[],
        ): ProviderPlanConfiguration | null => {
          const provider = providersById?.[providerId]?.result;
          const config = (configurations || []).find(
            configuration => configuration.plan_name === (provider as PrivilegedProvider)?.plan,
          );

          return config ? formatProviderPlanConfiguration([config])[0] : null;
        },
      );
    },

    planConfigurations() {
      return createSelector(
        slice(s => s.planConfigurations),
        (planConfigurations): Array<ProviderPlanConfiguration> => (
          formatProviderPlanConfiguration(planConfigurations)
        ),
      );
    },
  }),
});
